import {ChangeDetectorRef, Component, Input, OnInit, TemplateRef} from '@angular/core';
import {ArrayService, DcpEntryGroup} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {first} from 'rxjs/operators';
import {
  DATE_BUNDLE_TYPE,
  EntryGroupService,
  PRODUCT_BUNDLE_TYPE,
} from '../../../product/services/entry-group.service';
import {TermsConditionsService} from '../../../services/terms-conditions.service';
import {TransactionTypeService} from '../../../services/transactionTypes/transaction-type.service';
import {CheckoutEntryValidationService} from '../../services/checkout-entry-validation.service';

@UntilDestroy()
@Component({
  selector: 'dcp-entry-group',
  templateUrl: './entry-group.component.html',
  styleUrls: ['./entry-group.component.scss']
})
export class EntryGroupComponent implements OnInit {
  @Input() entryGroup: DcpEntryGroup;
  @Input() editionsInContractGroup: number;
  @Input() parentEntryGroup: DcpEntryGroup;
  @Input() parentGroupIndex: number;
  @Input() providerContractCount: number;
  @Input() derivativesTmpl: TemplateRef<any>;
  @Input() groupTotalTmpl: TemplateRef<any>;
  @Input() recurringTotalTmpl: TemplateRef<any>;
  @Input() providedOrderEntriesTmpl: TemplateRef<any>;
  @Input() paymentTmpl: TemplateRef<any>;
  @Input() termsAndConditionsTmpl: TemplateRef<any>;
  PRODUCT_BUNDLE_TYPE = PRODUCT_BUNDLE_TYPE;
  DATE_BUNDLE_TYPE = DATE_BUNDLE_TYPE;
  derivativeCount: number;
  hasInvalidEntry: boolean = false;
  termsConditionsEntries: OrderEntry[];

  constructor(
    public entryGroupService: EntryGroupService,
    public checkoutEntryValidationService: CheckoutEntryValidationService,
    public termsConditionsService: TermsConditionsService,
    private transactionTypeService: TransactionTypeService,
    public changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.derivativeCount = this.countDerivativesByIds(this.entryGroup);
    this.determineEditionsInEntryGroup();
    this.setUpTermsConditionsShowing();
  }

  determineEditionsInEntryGroup() {
    if (this.entryGroup.displayType === DATE_BUNDLE_TYPE) {
      this.editionsInContractGroup = this.entryGroupService.filterByGroupType(this.entryGroup.entryGroups).length;
    } else if (this.entryGroup.displayType === PRODUCT_BUNDLE_TYPE) {
      this.hasInvalidEntry = this.containsInvalidEntry(this.parentEntryGroup);
    }
  }

  containsRootGroups(entryGroup: DcpEntryGroup): boolean {
    if (!entryGroup?.entryGroups?.length) {
      return false;
    }
    return !!this.entryGroupService.filter(entryGroup.entryGroups, this.entryGroupService.isRoot).length;
  }

  /**
   * Counts the number of derivatives that are linked to the vendor.
   * Only the derivatives in the cart are taken into account.
   */
  countDerivativesByIds(contractGroup: DcpEntryGroup): number {
    const supplierAlternativeIds: string[] = this.entryGroupService.getDerivativeAlternativeIdsFromEntryGroups(contractGroup?.entryGroups);
    return ArrayService.removeDuplicates(supplierAlternativeIds).length;
  }

  /**
   * Getting editions as the only entries for T&Cs because we need edition-specific
   * CanonicalID.entitlementRuleId to show T&Cs by.
   *
   * @param entryGroups
   */
  getEntriesForTermsAndConditions(entryGroups: DcpEntryGroup[]): OrderEntry[] {
    return this.entryGroupService.getAllGroupEntries(this.entryGroupService.collectEntryGroups(
      (eg) => this.entryGroupService.isProduct(eg) || this.entryGroupService.isDerivative(eg)
        || this.entryGroupService.isEdition(eg) || this.entryGroupService.isAddon(eg), entryGroups
    ));
  }

  /**
   * Checks if the first entry of the first root group is invalid.
   *
   * @param rootGroupIndex Index of the parent entryGroup
   * @param hasInvalidEntry DcpEntryGroup to check for invalid order entries
   */
  isFirstRootEntryInvalid(rootGroupIndex: number, hasInvalidEntry: boolean): boolean {
    return rootGroupIndex === 0 && hasInvalidEntry;
  }

  /**
   * Check if entryGroup's no-derivative root bundles have invalid entries
   *
   * @param parentEntryGroup contract group to be checked
   */
  containsInvalidEntry(parentEntryGroup: DcpEntryGroup): boolean {
    const orderEntries = this.entryGroupService.getOrderEntriesByBundleType(
      parentEntryGroup.entryGroups, eg => !!eg.entries?.length
    );
    return this.checkoutEntryValidationService.containsInvalidEntries(orderEntries);
  }

  setUpTermsConditionsShowing() {
    this.termsConditionsEntries = this.getEntriesForTermsAndConditions(this.entryGroup.entryGroups);
    this.termsConditionsEntries.forEach(entry => {
      if (entry.product?.configurable) {
        this.handleTermsConditionsShowingError(entry);
        return;
      }
      this.transactionTypeService.isNetNew(entry.transactionType)
        .pipe(first(), untilDestroyed(this))
        .subscribe(isNetNew => {
          if (isNetNew) {
            this.termsConditionsService.getTermsConditionsByEntryProduct(entry).pipe(untilDestroyed(this)).subscribe((termsConditionsMap) => {
              if (termsConditionsMap?.error) {
                this.handleTermsConditionsShowingError(entry);
              }
            }, () => {
              this.handleTermsConditionsShowingError(entry);
            });
          }
        });
    });
  }

  private handleTermsConditionsShowingError(entry: OrderEntry) {
    this.termsConditionsEntries = this.termsConditionsEntries.filter(tcEntry => tcEntry !== entry);
    this.changeDetectorRef.detectChanges();
  }
}
